import {
  getPushRegistration,
  serializePushSubscription,
  supportsPushNotifications,
  unsubscribeFromPushNotifications,
  urlBase64ToUint8Array,
} from '@/lib/push-notifications';
import { NotificationService } from '@/services/notification.service';
import { useCallback, useEffect, useState } from 'react';

export type PushNotificationState = 'checking' | 'unsupported' | 'idle' | 'subscribed' | 'denied' | 'loading' | 'error';

export function usePushNotifications(enabled = true) {
  const [state, setState] = useState<PushNotificationState>('checking');

  const checkState = useCallback(async () => {
    if (!enabled) {
      setState('checking');
      return;
    }

    if (!supportsPushNotifications()) {
      setState('unsupported');
      return;
    }

    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }

    if (Notification.permission !== 'granted') {
      setState('idle');
      return;
    }

    const registration = await navigator.serviceWorker.getRegistration('/');
    const subscription = await registration?.pushManager.getSubscription();

    setState(subscription ? 'subscribed' : 'idle');
  }, [enabled]);

  useEffect(() => {
    void checkState().catch(() => setState('error'));
  }, [checkState]);

  const enablePush = useCallback(async () => {
    if (!supportsPushNotifications()) {
      setState('unsupported');
      return false;
    }

    setState('loading');

    try {
      const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();

      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'idle');
        return false;
      }

      const [{ publicKey }, registration] = await Promise.all([
        NotificationService.getWebPushPublicKey(),
        getPushRegistration(),
      ]);

      if (!publicKey) {
        throw new Error('As notificações em tempo real ainda não estão configuradas.');
      }

      const existingSubscription = await registration.pushManager.getSubscription();
      const subscription =
        existingSubscription ??
        // react-doctor-disable-next-line react-doctor/effect-needs-cleanup -- Browser-owned PushSubscription persists across unmounts; disablePush explicitly unsubscribes it.
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }));

      await NotificationService.subscribePush(serializePushSubscription(subscription));
      setState('subscribed');
      return true;
    } catch (error) {
      setState('error');
      throw error;
    }
  }, []);

  const disablePush = useCallback(async () => {
    setState('loading');

    try {
      await unsubscribeFromPushNotifications();
      setState('idle');
    } catch (error) {
      setState('error');
      throw error;
    }
  }, []);

  return {
    state,
    enablePush,
    disablePush,
    refreshState: checkState,
  };
}
